import React, { useCallback, useMemo, useRef } from 'react';
import { ArrowDropDown } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useStyles } from './../styles';
import { useAccountPreferences, useGroup, useGroupActions } from 'modules/scheduler/hooks';
import { PROJECT, RESOURCE } from 'modules/scheduler/enums/builderTypeEnum';
import { getViewObject } from 'modules/scheduler/utils/schedulerUtil';
import { SINGLE } from 'modules/scheduler/enums/viewModeEnum';
import { Button } from './styles';
import Popup from './popup';
import { useAppKeyWords, useIsExtensionInstalled } from 'hooks';
import { SMART_FILTERS } from 'enums/extensionShortIdEnum';
import { DefaultTooltipContent, Dialog, Tooltip, useOpenState } from '@hub-mono/ui';
import styled from 'styled-components';
import { useCanUserSeeExtensionsSettingsPage } from '@hub-mono/app-modules';

const StyledDialog = styled(Dialog)`
    .hub-ui__dialog-paper {
        padding: 32px;
    }
`;

const Filter = () => {
    const appKeywords = useAppKeyWords();
    const { filters } = useSelector(state => state.scheduler);
    const params = useParams();
    const { current } = useGroup(params);
    const { apply } = useGroupActions();
    const { isProjectView, isProjectGroupView, isSingleProjectView } = getViewObject(params);
    const { gridPreferences } = useAccountPreferences();
    const node = useRef();
    const canViewSettingExt = useCanUserSeeExtensionsSettingsPage();

    const isSmartFiltersExtensionInstalled = useIsExtensionInstalled(SMART_FILTERS);

    const classes = useStyles();

    const getNewFilters = useCallback(
        ({ dates, availability, utilization, operator }) => ({
            ...filters,
            smartFilters: {
                operator,
                filters: {
                    ...filters?.smartFilters?.filters,
                    availability: { ...availability },
                    utilization: { ...utilization },
                    dates,
                },
            },
        }),
        [filters]
    );

    const getDataToApply = useCallback(
        newFilters => ({
            group: current,
            isSmart: current.isSmart,
            itemType: isProjectView ? PROJECT : RESOURCE,
            filters: newFilters,
            groupsToAdd: [],
            groupsToRemove: [],
            itemsToAdd: [],
            itemsToRemove: [],
            parentGroupId: current.parentGroupId,
        }),
        [current, isProjectView]
    );

    const { open, isOpen, close, anchorElement } = useOpenState();

    const onApply = useCallback(
        ({ dates, availability, utilization, operator }) => {
            const newFilters = getNewFilters({ dates, availability, utilization, operator });
            const data = getDataToApply(newFilters);

            apply(data);
            close();
        },
        [apply, close, getDataToApply, getNewFilters]
    );

    const utilizationAndAvailabilityDisabled =
        (isProjectGroupView || isSingleProjectView) && gridPreferences.mode === SINGLE.value;

    const tooltip = useMemo(() => {
        if (!isSmartFiltersExtensionInstalled) {
            return {
                interactive: true,
                icon: 'unplug',
                header: 'Extension required',
                content: `Capacity Finder extension is required in order to use this functionality.`,
                actionButton1: canViewSettingExt
                    ? {
                          variant: 'outlined',
                          text: 'Go to Extensions',
                          endIcon: 'arrowForward',
                          onClick: e => {
                              e.preventDefault();
                              window.location.href = '/settings#extensions';
                          },
                      }
                    : null,
            };
        }
        if (isSmartFiltersExtensionInstalled && utilizationAndAvailabilityDisabled) {
            return {
                header: 'Capacity Finder disabled',
                content: `Capacity Finder disabled in ${appKeywords.projectKeyWord} view mode, single rows (${appKeywords.projectKeyWord} & ${appKeywords.resourceKeyWord}). Switch view mode to apply availability or utilization filters. `,
            };
        }
    }, [
        appKeywords.projectKeyWord,
        appKeywords.resourceKeyWord,
        canViewSettingExt,
        isSmartFiltersExtensionInstalled,
        utilizationAndAvailabilityDisabled,
    ]);

    const isFilterOn =
        Number.isInteger(Number.parseFloat(filters?.smartFilters?.filters?.utilization?.value)) ||
        Number.isInteger(Number.parseFloat(filters?.smartFilters?.filters?.availability?.value));

    const CapacityButtonFinder = (
        <Button
            data-cy="scheduler-nav-buttons-utilization-availability"
            aria-controls="schedule-menu"
            aria-haspopup="true"
            classes={classes}
            onClick={open}
            className={isFilterOn ? 'filter-active' : ''}
            endIcon={<ArrowDropDown />}
            disableRipple
            disabled={!isSmartFiltersExtensionInstalled || utilizationAndAvailabilityDisabled}
        >
            Capacity Finder
        </Button>
    );

    const title = undefined;
    const content = <div ref={node}> {filters && <Popup onApply={onApply} toggleDatePicker={close} />}</div>;
    const actions = undefined;

    return (
        <>
            {tooltip ? (
                <Tooltip
                    interactive={tooltip.interactive}
                    title={
                        <DefaultTooltipContent
                            icon={tooltip.icon}
                            header={tooltip.header}
                            content={tooltip.content}
                            actionButton1={tooltip.actionButton1}
                        />
                    }
                >
                    <span>{CapacityButtonFinder}</span>
                </Tooltip>
            ) : (
                CapacityButtonFinder
            )}
            {isOpen ? (
                <StyledDialog
                    open
                    title={title}
                    content={content}
                    actions={actions}
                    onClose={close}
                    hideCloseButton={true}
                    anchorElement={anchorElement}
                    level="default"
                />
            ) : null}
        </>
    );
};

export default Filter;
